import { offlineAppService } from '@/lib/offline/offlineAppService';
import { BaseRecord } from '@refinedev/core';
import { create } from 'zustand';

export interface OfflineAppStoreConfig {
  storageKey: string;
}

export interface MethodExecutionResult {
  methodKey: string;
  result: any;
  status: 'pending' | 'completed' | 'error';
  error?: string;
}

// A macOS Text Replacement entry from `getSystemTextReplacements`.
// `on` = enabled flag (1 = on, 0 = off). Fields are optional since the
// native payload is student-controlled and may be partial.
export interface SystemTextReplacement {
  on?: number;
  replace?: string;
  with?: string;
}

export interface OfflineAppState extends BaseRecord {
  isOffline: boolean;
  pendingActions: any[];
  loading: boolean;
  error: null | string;
  methodResults: Record<string, MethodExecutionResult>;
  firebaseConnectionClosed: boolean;
  // Snapshot of the student's macOS Text Replacements, captured once at
  // test start. Saved for later telemetry use. `null` until captured,
  // on non-Mac builds, and on bridge failure.
  systemTextReplacements: SystemTextReplacement[] | null;
}

interface OfflineAppActions {
  setOfflineStatus: (status: boolean) => void;
  addPendingAction: (action: any) => void;
  clearPendingActions: () => void;
  executeMethods: (
    methods: Array<{
      key: string;
      value?: any;
      onSuccess?: (result: any) => void;
      onError?: (error: any) => void;
    }>
  ) => Promise<Record<string, any>>;
  setMethodResult: (methodKey: string, result: MethodExecutionResult) => void;
  clearMethodResults: () => void;
  setFirebaseConnectionClosed: (status: boolean) => void;
  setSystemTextReplacements: (items: SystemTextReplacement[] | null) => void;
}

type OfflineAppStore = OfflineAppState & OfflineAppActions & { config: OfflineAppStoreConfig };

export const offlineAppStoreConfig: OfflineAppStoreConfig = {
  storageKey: 'offlineAppState',
};

export const useOfflineAppStore = create<OfflineAppStore>((set, get) => ({
  isOffline: false,
  pendingActions: [],
  loading: false,
  error: null,
  methodResults: {},
  config: offlineAppStoreConfig,
  firebaseConnectionClosed: false,
  systemTextReplacements: null,

  setOfflineStatus: (status: boolean) => set({ isOffline: status }),

  addPendingAction: (action: any) =>
    set((state) => ({ pendingActions: [...state.pendingActions, action] })),

  clearPendingActions: () => set({ pendingActions: [] }),

  setMethodResult: (methodKey: string, result: MethodExecutionResult) =>
    set((state) => ({
      methodResults: {
        ...state.methodResults,
        [methodKey]: result,
      },
    })),

  clearMethodResults: () => set({ methodResults: {} }),

  executeMethods: async (methods) => {
    const results: Record<string, any> = {};

    for (const method of methods) {
      try {
        set((state) => ({
          methodResults: {
            ...state.methodResults,
            [method.key]: {
              methodKey: method.key,
              status: 'pending',
              result: null,
            },
          },
        }));

        const result = await offlineAppService.executeAppMethod({
          key: method.key,
          value: method.value,
        });

        results[method.key] = result;
        if (method.key === 'getSecurityStatus') {
          set((state) => ({
            methodResults: {
              ...state.methodResults,
              changeSecurity: {
                methodKey: method.key,
                status: 'completed',
                result,
              },
            },
          }));
        }
        set((state) => ({
          methodResults: {
            ...state.methodResults,
            [method.key]: {
              methodKey: method.key,
              status: 'completed',
              result,
            },
          },
        }));
        method.onSuccess?.(result);
      } catch (error) {
        set((state) => ({
          methodResults: {
            ...state.methodResults,
            [method.key]: {
              methodKey: method.key,
              status: 'error',
              result: null,
              error: error instanceof Error ? error.message : 'Unknown error',
            },
          },
        }));
        results[method.key] = null;
        method.onError?.(error);
      }
    }

    return results;
  },
  setFirebaseConnectionClosed: (status: boolean) => set({ firebaseConnectionClosed: status }),

  setSystemTextReplacements: (items: SystemTextReplacement[] | null) =>
    set({ systemTextReplacements: items }),
}));

export const selectSystemTextReplacements = (s: OfflineAppStore): SystemTextReplacement[] | null =>
  s.systemTextReplacements;
